import React, { useEffect, useRef, memo } from 'react';
import { VerticalScrollerCore } from './VerticalScroller.core';
import { cn } from '../../utils/cn';

export interface VerticalScrollerProps {
  /**
   * Children must be React elements with data-vertical-scroller-id
   */
  children: React.ReactNode;

  /**
   * Current pane ID to display (controlled mode)
   */
  currentPaneId?: string;

  /**
   * Initial pane ID to focus on (uncontrolled mode)
   * @deprecated Use currentPaneId for controlled behavior
   */
  initialId?: string;

  /**
   * Callback when pane changes
   */
  onPaneChange?: (id: string) => void;

  /**
   * Container className
   */
  className?: string;

  /**
   * Height of the container (default: 100vh)
   */
  height?: string | number;

  /**
   * Enable keyboard navigation (default: true)
   * When true, arrow keys and page up/down will navigate between panes
   */
  enableKeyboardNav?: boolean;
}

/**
 * React wrapper for the high-performance VerticalScroller
 *
 * Can be used in controlled or uncontrolled mode:
 * - Controlled: Use `currentPaneId` prop
 * - Uncontrolled: Use `initialId` prop (deprecated)
 *
 * @example
 * ```tsx
 * // Controlled usage (recommended)
 * <VerticalScroller
 *   currentPaneId={selectedPhotoId}
 *   onPaneChange={(id) => setSelectedPhotoId(id)}
 * >
 *   <div data-vertical-scroller-id="pane-1">Content 1</div>
 *   <div data-vertical-scroller-id="pane-2">Content 2</div>
 * </VerticalScroller>
 * ```
 */
export const VerticalScroller = memo<VerticalScrollerProps>(
  ({ children, currentPaneId, initialId, onPaneChange, className, height = '100vh', enableKeyboardNav = true }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<VerticalScrollerCore | null>(null);

    // Initialize the core scroller once
    useEffect(() => {
      if (!containerRef.current) return;

      const scroller = new VerticalScrollerCore(containerRef.current, '');
      scrollerRef.current = scroller;
      
      // Activate the scroller for keyboard events if enabled
      if (enableKeyboardNav) {
        scroller.setActive(true);
      }

      // Set up event handler
      if (onPaneChange) {
        scroller.on('set', onPaneChange);
      }

      return () => {
        scroller.destroy();
        scrollerRef.current = null;
      };
    }, []); // Only run once on mount
    
    // Update active state when prop changes
    useEffect(() => {
      if (scrollerRef.current) {
        scrollerRef.current.setActive(enableKeyboardNav);
      }
    }, [enableKeyboardNav]);

    // Handle controlled mode - scroll when currentPaneId changes
    useEffect(() => {
      if (!scrollerRef.current || !currentPaneId) return;

      // Scroll to the specified pane immediately
      scrollerRef.current.setPane(currentPaneId, true);
    }, [currentPaneId]);

    // Handle uncontrolled mode - set initial position once
    useEffect(() => {
      if (!scrollerRef.current || currentPaneId !== undefined) return;

      // Only use initialId if we're in uncontrolled mode
      if (initialId) {
        scrollerRef.current.setPane(initialId, true);
      }
    }, []); // Only run once on mount

    return (
      <div
        ref={containerRef}
        className={cn('vertical-scroller-container', className)}
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          position: 'relative',
          overflow: 'hidden',
          touchAction: 'none', // Prevent browser scroll interference
        }}
      >
        {children}
      </div>
    );
  }
);

VerticalScroller.displayName = 'VerticalScroller';

/**
 * Pane component for semantic usage
 */
export interface PaneProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  onVisible?: () => void;
}

export const Pane = memo<PaneProps>(({ id, children, className, onVisible }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onVisible || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onVisible();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [onVisible]);

  return (
    <div
      ref={ref}
      data-vertical-scroller-id={id}
      className={cn('vertical-scroller-pane', className)}
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {children}
    </div>
  );
});

Pane.displayName = 'Pane';
